import { useCallback, useEffect } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { observer } from 'mobx-react'

import { RentalDetailsCard } from 'Modules/rentals/components/RentalDetailsCard/RentalDetailsCard'
import { RentalPresentationCard } from 'Modules/rentals/components/RentalPresentationCard/RentalPresentationCard'

import { ContentSidebar } from 'Components/ContentSidebar/ContentSidebar'
import { DeviceHealthCard } from 'Components/DeviceHealth/DeviceHealthCard'
import { DeviceHistoryEntityCard } from 'Components/DeviceHistoryEntityCard/DeviceHistoryEntityCard'
import { useDeviceInfoEnabled } from 'Components/DeviceInfo/useDeviceInfoEnabled'
import { EmployeeEntityCard } from 'Components/EmployeeEntityCard/EmployeeEntityCard'
import { PageErrorState } from 'Components/PageErrorState/PageErrorState'
import { PageHeader } from 'Components/PageHeader/PageHeader'
import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'
import { ParentChildRentalsEntityCard } from 'Components/ParentChildRentalsEntityCard/ParentChildRentalsEntityCard'
import { PhonePlansEntityCard } from 'Components/PhonePlansEntityCard/PhonePlansEntityCard'
import { RecentRequestsEntityCard } from 'Components/RecentRequestsEntityCard/RecentRequestsEntityCard'

import { useStore } from 'Stores/index'

import { PortalSectionEnum } from 'Constants/portalSection'

export const Rental = observer(() => {
  const { rentalStore } = useStore()
  const {
    rentalDetails,
    fetchRentalDetails,
    isLoadingRentalDetails,
    isRentalDetailsError,
  } = rentalStore
  const { employeeId, organisationId, rentalId } = useParams<{
    employeeId?: string
    organisationId?: string
    rentalId?: string
  }>()
  const isOrganisation = !!organisationId
  const isDeviceInfoEnabled = useDeviceInfoEnabled()

  const handleRentalDetailsFetch = useCallback((): Promise<void> => {
    if (!rentalId) {
      return Promise.resolve()
    }

    return fetchRentalDetails({
      employeeId,
      isOrganisationView: isOrganisation,
      organisationId,
      rentalID: rentalId,
    })
  }, [employeeId, fetchRentalDetails, isOrganisation, organisationId, rentalId])

  useEffect(() => {
    handleRentalDetailsFetch()
  }, [handleRentalDetailsFetch])

  // Show the skeleton on initial load and when navigating between different
  // rentals. Background refetches (e.g. tag add/remove) keep the tree mounted
  // so in-flight UI state (tag editor, focus, toast) is preserved.
  // Stringify both ids: codegen types rental.id as string but the BE returns a
  // number at runtime, so a strict !== would always trigger and flash the
  // skeleton during refetches.
  const isInitialOrNavigationLoad =
    isLoadingRentalDetails &&
    (!rentalDetails || String(rentalDetails.rental.id) !== rentalId)

  if (isInitialOrNavigationLoad) {
    return <PageLoadingState />
  }

  if (isRentalDetailsError || !rentalDetails) {
    return <PageErrorState />
  }

  const { rental } = rentalDetails

  return (
    <Container className="pt-4 pt-lg-6" fluid>
      <div role="banner">
        <PageHeader
          className="mb-4 mb-lg-6"
          portalSection={PortalSectionEnum.RentalDetails}
        />
      </div>

      <Row>
        <Col xxl={8}>
          <RentalPresentationCard
            deviceDefinition={rental?.deviceDefinition}
            stocklistItem={rental.stocklistItem}
            title="Device"
          />

          <RentalDetailsCard
            refetch={handleRentalDetailsFetch}
            rental={rental}
          />
        </Col>

        <ContentSidebar
          isSidebarOpen={false}
          renderStickyColumnSideContent={
            <Row>
              <Col xs={12}>
                <ParentChildRentalsEntityCard
                  rentalChildren={rental.children}
                  rentalParent={rental.parent}
                />

                <RecentRequestsEntityCard
                  caseItems={rental.caseItems?.caseItems ?? []}
                />
              </Col>

              <div className="d-block d-lg-flex flex-wrap d-xxl-block w-100">
                {isOrganisation && (
                  <Col
                    className="flex-shrink-1 me-0 me-lg-6 me-xxl-0"
                    lg={6}
                    xs={12}
                    xxl={12}
                  >
                    <EmployeeEntityCard employee={rental.employee ?? null} />
                  </Col>
                )}

                <Col
                  className="flex-shrink-1 me-0 me-lg-6 me-xxl-0"
                  lg={6}
                  xs={12}
                  xxl={12}
                >
                  <DeviceHistoryEntityCard rental={rental} />
                </Col>

                {isOrganisation && isDeviceInfoEnabled && (
                  <Col className="flex-shrink-1" lg={6} xs={12} xxl={12}>
                    <DeviceHealthCard
                      organisationId={organisationId}
                      rentalId={rentalId ?? '0'}
                    />
                  </Col>
                )}
              </div>

              {!!rental.employee?.phonePlans?.phonePlans?.length && (
                <Col lg={6} xs={12} xxl={12}>
                  <PhonePlansEntityCard
                    phonePlans={rental.employee.phonePlans.phonePlans}
                  />
                </Col>
              )}
            </Row>
          }
        />
      </Row>
    </Container>
  )
})
